import { useState, useEffect } from 'react';
import {
  Video,
  Mic,
  MessageCircle,
  CalendarClock,
  CreditCard,
  Send,
  CheckCircle,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import Services from '../lib/services';
const DURATION_OPTIONS = [
  { label: '15 min',  value: 15,  multiplier: 0.25 },
  { label: '30 min',  value: 30,  multiplier: 0.5  },
  { label: '45 min',  value: 45,  multiplier: 0.75 },
  { label: '1 hr',    value: 60,  multiplier: 1    },
  { label: '1:30 hr', value: 90,  multiplier: 1.5  },
  { label: '2 hr',    value: 120, multiplier: 2    },
];

const CONSULTATION_PRICING_GUIDE = [
  { key: 'chat', label: 'Messaging / Chat', range: 'US$2 to $5', recommended: 3 },
  { key: 'audio', label: 'Audio Consultation', range: 'US$4 to $8', recommended: 5 },
  { key: 'video', label: 'Video Consultation', range: 'US$6 to $12', recommended: 8 },
  { key: 'follow-up-chat', label: 'Follow-up Messaging', range: 'US$1 to $3', recommended: 2 },
  { key: 'follow-up-audio', label: 'Follow-up Audio', range: 'US$2 to $5', recommended: 3 },
  { key: 'follow-up-video', label: 'Follow-up Video', range: 'US$3 to $7', recommended: 5 },
];

export default function BookingScreen() {
 const {profile} = useAuth();
  console.log(profile)
  const user = profile
  const [formData, setFormData] = useState({
    mode: 'video',
    timing: 'later',
    scheduledTime: '',
    paymentMethod: '',
    note: '',
    duration: 30,
  });
  const [walletError, setWalletError] = useState('');
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDateIndex, setSelectedDateIndex] = useState(0);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [showSuccessSheet, setShowSuccessSheet] = useState(false);
  const [doctorLoading, setDoctorLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const doctorFromState = location.state && typeof location.state === 'object' ? location.state : null;
  const [doctor, setDoctor] = useState(() => {
    if (doctorFromState) return doctorFromState;

    const storedDoctor = localStorage.getItem('selectedDoctor');
    if (!storedDoctor) return null;

    try {
      return JSON.parse(storedDoctor);
    } catch (err) {
      console.error('Invalid selectedDoctor in localStorage:', err);
      return null;
    }
  });

  const storedUser = localStorage.getItem("user");
  let u_d = null;
  try {
    u_d = storedUser ? JSON.parse(storedUser) : null;
  } catch (err) {
    console.error('Invalid user data in localStorage:', err);
  }

  const u_e = u_d?.email || profile?.email || '';





console.log(doctor?._id, user?._id)
  const resolvedPatientId = localStorage.getItem('userId');

  const resolvedPatient = resolvedPatientId

  const selectedDurationOption = DURATION_OPTIONS.find(d => d.value === formData.duration) || DURATION_OPTIONS[1];
  const activePriceGuide = CONSULTATION_PRICING_GUIDE.find(item => item.key === formData.mode) || CONSULTATION_PRICING_GUIDE[2];
  const fallbackDoctorPrice = Number(doctor?.price) || 150;
  const basePrice = Number(activePriceGuide?.recommended ?? fallbackDoctorPrice) || fallbackDoctorPrice;
  const consultationPrice = Math.round(basePrice * selectedDurationOption.multiplier);

  useEffect(() => {
    if (!doctorFromState?._id) return;

    setDoctor(doctorFromState);
    localStorage.setItem('selectedDoctor', JSON.stringify(doctorFromState));
  }, [doctorFromState]);

  useEffect(() => {
    let isCancelled = false;

    const fetchDoctorIfNeeded = async () => {
      if (!doctor?._id) return;

      const hasCoreDoctorInfo = Boolean(
        doctor?.name && doctor?.specialty && doctor?.availability
      );

      if (hasCoreDoctorInfo) return;

      setDoctorLoading(true);
      try {
        const res = await api.get(`/api/doctor/${doctor._id}`);
        const fullDoctor = res?.data;
        if (!isCancelled && fullDoctor?._id) {
          setDoctor(fullDoctor);
          localStorage.setItem('selectedDoctor', JSON.stringify(fullDoctor));
        }
      } catch (err) {
        console.error('Failed to fetch doctor details:', err);
      } finally {
        if (!isCancelled) {
          setDoctorLoading(false);
        }
      }
    };

    fetchDoctorIfNeeded();

    return () => {
      isCancelled = true;
    };
  }, [doctor?._id]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Build available dates + slots from doctor.availability
  useEffect(() => {
    if (!doctor || !doctor.availability) return setAvailableDates([]);

    // helper: generate next N dates
    const getNextNDates = (n) => {
      const arr = [];
      const now = new Date();
      for (let i = 0; i < n; i++) {
        const d = new Date(now);
        d.setDate(now.getDate() + i);
        arr.push(d);
      }
      return arr;
    };

    // helper: split range 'HH:MM-HH:MM' into slots (30 min)
    const rangeToSlots = (range, slotMinutes = 30) => {
      const [startStr, endStr] = range.split('-');
      const [sh, sm] = startStr.split(':').map(Number);
      const [eh, em] = endStr.split(':').map(Number);
      const slots = [];
      // use arbitrary date base
      const base = new Date();
      base.setHours(sh, sm, 0, 0);
      const end = new Date();
      end.setHours(eh, em, 0, 0);
      for (let t = new Date(base); t < end; t = new Date(t.getTime() + slotMinutes * 60000)) {
        const hh = String(t.getHours()).padStart(2, '0');
        const mm = String(t.getMinutes()).padStart(2, '0');
        slots.push(`${hh}:${mm}`);
      }
      return slots;
    };

    const next14 = getNextNDates(14);
    const byDate = next14.map((d) => {
      const weekday = d.toLocaleDateString(undefined, { weekday: 'long' }).toLowerCase();
      const ranges = doctor.availability?.[weekday] || [];
      const slots = ranges.flatMap(r => rangeToSlots(r, 30));
      return { date: d, slots, ranges };
    });

    // filter dates with slots
    const withSlots = byDate.filter(d => d.slots && d.slots.length > 0);
    setAvailableDates(withSlots);
    setSelectedDateIndex(0);
  }, [doctor]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!doctor?._id || !resolvedPatientId) {
      alert('Missing booking details. Please select a doctor and try again.');
      setLoading(false);
      return;
    }

     const doctor_main = {
    email : doctor.email,
    phone : doctor.phone,
    name : doctor.name,
  }

    try {
      // validate scheduled time is present
      if (!formData.scheduledTime) {
        alert('Please choose an appointment time.');
        setLoading(false);
        return;
      }
        const data = {
        userId: resolvedPatientId,
        providerId: "6996f83cad941d32a067a6f4",
        amount: consultationPrice,
        type: "consultation",
        serviceDetails: `Doctor Consultation, ${doctor.specialty} (${selectedDurationOption.label})`
        }
        console.log(data, "consultation data")
    const payment =   await Services.wallet.payProvider(data)
    console.log(payment, "payment response");
    if (!payment?.success) {
      const msg = payment?.message || 'Payment failed.';
      const isInsufficient = /insufficient/i.test(msg);
      if (isInsufficient) {
        setWalletError('insufficient');
      } else {
        setWalletError(msg);
      }
      setLoading(false);
      return;
    }
    setWalletError('');

      console.log(resolvedPatientId, resolvedPatient, "comparing Ids")

      const payload = {
        patient: resolvedPatientId,
        doctor: doctor._id,
        patient_ : resolvedPatient,
        doctor_ : doctor_main,
        patientEmail : u_e || resolvedPatient?.email || '',
        mode: formData.mode,
        appointmentTime: formData.scheduledTime,
        duration: formData.duration,
        amount: consultationPrice,
        reason: formData.note,
      };

        if(payment.success){
      const res = await api.post('/api/consultations', payload);

      console.log('Consultation created:', res.data);
      setConfirmation(res.data); // Set confirmation data to show
      // A chat booking is ready to use immediately. Take the patient straight
      // into the persistent consultation thread rather than leaving them on a
      // generic confirmation screen.
      if (formData.mode === 'chat') {
        navigate(`/consultation-chats/${res.data._id}`, { state: { consultation: res.data, doctor } });
        return;
      }
      setShowSuccessSheet(true);

      // Log activity
      try {
        await api.post('/api/activities/add-activity', { message: 'Booked a doctor consultation' });
      } catch (err) {
        console.error('Failed to log booking activity', err);
      }
        }
    } catch (err) {
      console.error('Error creating consultation:', err);
      alert('Failed to book consultation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-4" style={{ maxWidth: 600 }}>
      {walletError === 'insufficient' && (
        <div
          className="alert d-flex align-items-center justify-content-between"
          role="alert"
          style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 12, marginBottom: 16 }}
        >
          <span style={{ fontSize: 13, color: '#664d03' }}>
            Insufficient Wallet Balance &mdash; please top up your wallet.
          </span>
          <button
            type="button"
            className="btn btn-sm fw-semibold"
            onClick={() => navigate('/health-wallet')}
            style={{ background: 'var(--primary)', color: '#fff', borderRadius: 8, fontSize: 12 }}
          >
            Top Up
          </button>
        </div>
      )}
      {walletError && walletError !== 'insufficient' && (
        <div className="alert alert-danger" role="alert" style={{ borderRadius: 12, fontSize: 13 }}>
          {walletError}
        </div>
      )}
      {!doctor ? (
        <div className="alert alert-warning d-flex justify-content-between align-items-center" role="alert">
          <span>Doctor details are missing. Please choose a doctor again.</span>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => navigate(-1)}
            style={{ borderColor: 'var(--primary)', color: 'var(--primary)' }}
          >
            Go back
          </button>
        </div>
      ) : null}

      {doctorLoading ? (
        <div className="alert alert-info" role="alert">
          Loading doctor details...
        </div>
      ) : null}

      {/* Header */}
      <div className="d-flex align-items-center mb-4">
        <button
          type="button"
          className="btn btn-link p-0 me-2"
          onClick={() => navigate(-1)}
          style={{ color: 'var(--primary)', textDecoration: 'none' }}
        >
          <i className="fi fi-br-angle-left" style={{ fontSize: 20, color: 'var(--primary)' }}></i>
        </button>
        <h5 className="fw-semibold mb-0 " style = {{color : "var(--primary)"}}>Book with {doctor?.name || 'Doctor'}</h5>
      </div>

      {/* Doctor Card */}
      <div className="card shadow-sm mb-4 p-3 d-flex flex-row align-items-center" style={{ borderRadius: 16, border: '1px solid rgba(127, 0, 255, 0.14)', background: 'linear-gradient(135deg, rgba(127, 0, 255, 0.08), #ffffff)' }}>
        <img
          src={doctor?.avatar}
          alt={doctor?.name || 'Doctor'}
          className="rounded-circle me-3"
          style={{ width: 70, height: 70, objectFit: 'cover' }}
        />
        <div>
          <h6 className="fw-bold mb-1">{doctor?.name || 'Doctor'}</h6>
          <p className="text-muted mb-1" style={{ fontSize: 13 }}>{doctor?.specialty || 'Specialty not available'}</p>
          <p className="text-muted mb-1" style={{ fontSize: 13 }}>⭐ {doctor?.rating || 5.0} Rating</p>
          <p className="text-success fw-medium mb-0" style={{ fontSize: 13 }}>${doctor?.price || '--'} • Covered by Insurance</p>
        </div>
      </div>

      {/* Booking Form */}
      {!confirmation ? (
        <form onSubmit={handleSubmit}>
          {/* Consultation Duration */}
          <div className="mb-4">
            <label className="fw-medium mb-2 d-block">Consultation Duration</label>
            <div className="d-flex gap-2 flex-wrap">
              {DURATION_OPTIONS.map(({ label, value }) => (
                <button
                  key={value}
                  type="button"
                  className={`btn btn-sm flex-grow-1 ${formData.duration === value ? '' : 'btn-outline-secondary'}`}
                  onClick={() => handleChange('duration', value)}
                  style={{
                    borderRadius: 10,
                    fontSize: 13,
                    padding: '8px 10px',
                    borderColor: formData.duration === value ? 'var(--primary)' : undefined,
                    background: formData.duration === value ? 'var(--primary)' : undefined,
                    color: formData.duration === value ? '#fff' : undefined,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="text-muted mt-2 mb-0" style={{ fontSize: 13 }}>
              Estimated cost: <strong>${consultationPrice}</strong>
            </p>
          </div>

          {/* Mode of Consultation */}
          <div className="mb-4">
            <label className="fw-medium mb-2 d-block">Mode of Consultation</label>
            <div className="d-flex gap-2 flex-wrap">
              {[
                { key: 'video', label: 'Video', icon: <Video size={16} /> },
                { key: 'audio', label: 'Audio', icon: <Mic size={16} /> },
                { key: 'chat', label: 'Chat', icon: <MessageCircle size={16} /> },
              ].map(({ key, label, icon }) => (
                <button
                  key={key}
                  type="button"
                  className={`btn ${formData.mode === key ? '' : 'btn-outline-secondary'} btn-sm flex-grow-1`}
                  onClick={() => handleChange('mode', key)}
                  style={{
                    borderRadius: 10,
                    fontSize: 13,
                    padding: '8px 10px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 6,
                    borderColor: formData.mode === key ? 'var(--primary)' : undefined,
                    background: formData.mode === key ? 'var(--primary)' : undefined,
                    color: formData.mode === key ? '#fff' : undefined,
                  }}
                >
                  {icon} {label}
                </button>
              ))}
            </div>

            <div className="mt-3 border rounded-3 p-3" style={{ background: 'rgba(127, 0, 255, 0.03)', borderColor: 'rgba(127, 0, 255, 0.12)' }}>
              <div className="fw-semibold mb-2" style={{ fontSize: 12, color: 'var(--primary)' }}>
                Suggested consultation pricing
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                {CONSULTATION_PRICING_GUIDE.map((item) => (
                  <div
                    key={item.key}
                    className="rounded-3 p-2"
                    style={{
                      background: item.key === formData.mode ? 'rgba(127, 0, 255, 0.09)' : '#fff',
                      border: item.key === formData.mode ? '1px solid rgba(127, 0, 255, 0.25)' : '1px solid rgba(15, 23, 42, 0.07)',
                    }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                      {item.label}
                    </div>
                    <div style={{ fontSize: 12, color: '#0f172a' }}>
                      {item.range}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--primary)' }}>
                      Starts at ${item.recommended}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Appointment Timing */}
          <div className="mb-4">
            <label className="fw-medium mb-2 d-block">Appointment Timing</label>
            <div className="mt-3">
              {availableDates && availableDates.length > 0 ? (
                <>
                  <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 6 }}>
                    {availableDates.map((d, i) => (
                      <div key={i} onClick={() => { setSelectedDateIndex(i); setSelectedSlot(null); }} style={{ minWidth: 96, padding: 10, borderRadius: 12, textAlign: 'center', cursor: 'pointer', background: i === selectedDateIndex ? 'var(--primary)' : '#fff', color: i === selectedDateIndex ? '#fff' : 'var(--on-bg)', boxShadow: i === selectedDateIndex ? '0 8px 20px rgba(127, 0, 255, 0.18)' : 'none' }}>
                        <div style={{ fontSize: 12 }}>{new Date(d.date).toLocaleDateString(undefined, { weekday: 'short' })}</div>
                        <div style={{ fontWeight: 700 }}>{new Date(d.date).getDate()}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {(availableDates[selectedDateIndex]?.slots || []).map((slot, idx) => (
                      <button type="button" key={idx} className={`btn btn-sm ${selectedSlot === slot ? 'btn-accent' : 'btn-outline-secondary'}`} onClick={() => {
                        // build ISO datetime from selected date and slot
                        const d = availableDates[selectedDateIndex].date;
                        const [hh, mm] = slot.split(':');
                        const dt = new Date(d);
                        dt.setHours(Number(hh), Number(mm), 0, 0);
                        const iso = dt.toISOString();
                        handleChange('scheduledTime', iso);
                        setSelectedSlot(slot);
                      }} style={{ borderRadius: 999 }}>{slot}</button>
                    ))}
                  </div>

                  {selectedSlot && <div className="mt-2 text-muted">Selected: {new Date(formData.scheduledTime).toLocaleString()}</div>}
                  {availableDates[selectedDateIndex]?.slots?.length > 6 && <div className="mt-2"><button style={{color : "var(--primary)"}} className="btn btn-link p-0">View all availability</button></div>}
                </>
              ) : (
                <div>
                  <p className="text-muted">No predefined slots available for this doctor. Pick a date and time:</p>
                  <input
                    type="datetime-local"
                    className="form-control input-theme"
                    value={formData.scheduledTime}
                    onChange={(e) => handleChange('scheduledTime', e.target.value)}
                    required
                    style={{ fontSize: 13, borderRadius: 10 }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Payment Method */}
          <div className="mb-4">
            <label className="fw-medium mb-2 d-block">Payment Method</label>
            <div className="d-flex gap-2 flex-wrap">
              {['Health Wallet', 'Dependent Wallet'].map((method) => (
                <button
                  key={method}
                  type="button"
                  className={`btn ${formData.paymentMethod === method ? '' : 'btn-outline-secondary'} btn-sm flex-grow-1`}
                  onClick={() => handleChange('paymentMethod', method)}
                  style={{
                    borderRadius: 10,
                    fontSize: 13,
                    padding: '8px 10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    borderColor: formData.paymentMethod === method ? 'var(--primary)' : undefined,
                    background: formData.paymentMethod === method ? 'var(--primary)' : undefined,
                    color: formData.paymentMethod === method ? '#fff' : undefined,
                  }}
                >
                  <CreditCard size={14} /> {method}
                </button>
              ))}
            </div>
          </div>

          {/* Reason for Visit */}
          <div className="mb-4">
            <label className="fw-medium mb-2 d-block">Reason for Visit</label>
            <textarea
              className="form-control input-theme"
              rows={3}
              placeholder="Briefly describe your reason for visit"
              value={formData.note}
              onChange={(e) => handleChange('note', e.target.value)}
              required
              style={{ fontSize: 13, borderRadius: 12, padding: 10 }}
            />
          </div>

          {/* Submit Button */}
          <div className="d-flex flex-column gap-2">
            <button
              type="submit"
              className="btn w-100 py-2 fw-semibold"
              disabled={loading || doctorLoading || !doctor?._id}
              style={{ borderRadius: 12, fontSize: 14, display: 'flex', alignItems: 'center', color : "white", background: "var(--primary)", borderColor: 'var(--primary)', justifyContent: 'center', gap: 6 }}
            >
              <Send size={16} /> {loading ? 'Booking...' : `Confirm & Pay $${consultationPrice}`}
            </button>
          </div>
        </form>
      ) : (
        // Confirmation Card
        <div className="card card-theme shadow-sm mb-4 p-3" style={{ borderRadius: 16 }}>
          <p className="mb-1" style={{ fontSize: 14 }}>
            <CalendarClock size={16} className="me-2 accent" />
            Your appointment with <strong>{doctor.name}</strong> is set for{' '}
            <strong>{new Date(confirmation.appointmentTime).toLocaleString()}</strong>.
          </p>
          <p className="text-muted mb-2" style={{ fontSize: 13 }}>
            You’ll get a reminder 15 mins before the session.
          </p>

          <p className="mb-2" style={{ fontSize: 14 }}>
            <strong>Room ID:</strong> {confirmation.roomId}
          </p>

          <button
            className="btn w-100"
            onClick={() => navigate(formData.mode === 'chat' ? `/consultation-chats/${confirmation._id}` : '/doctor-consult', { state: { consultation: confirmation, doctor } })}
            style={{ background: 'var(--primary)', borderColor: 'var(--primary)', color: '#fff' }}
          >
            {formData.mode === 'chat' ? <MessageCircle size={16} className="me-1" /> : <Video size={16} className="me-1" />} {formData.mode === 'chat' ? 'Open chat' : 'Ok'}
          </button>
        </div>
      )}

      {showSuccessSheet && confirmation && (
        <>
          <div
            onClick={() => setShowSuccessSheet(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(15, 12, 24, 0.45)",
              zIndex: 1200,
            }}
          />
          <div
            style={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 0,
              background: "#ffffff",
              borderTopLeftRadius: 28,
              borderTopRightRadius: 28,
              padding: "14px 16px 24px",
              zIndex: 1201,
              textAlign: "center",
            }}
          >
            <div style={{ width: 46, height: 5, borderRadius: 999, background: "#ece8f5", margin: "0 auto 18px" }} />
            <div style={{ width: 40, height: 40, borderRadius: 999, margin: "0 auto 12px", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--primary)" }}>
              <CheckCircle size={18} color="#fff" />
            </div>
            <h3 style={{ margin: "0 0 8px", fontSize: 34, fontWeight: 700, color: "#15121f" }}>Congratulations!!!</h3>
            <p style={{ margin: "0 0 18px", color: "#6f6a7d", fontSize: 16, lineHeight: 1.45 }}>
              Congratulations, your consultation booking has been placed successfully.
            </p>
            <button
              type="button"
              onClick={() => navigate(formData.mode === 'chat' ? `/consultation-chats/${confirmation._id}` : '/doctor-consult', { state: { consultation: confirmation, doctor } })}
              style={{
                width: "100%",
                border: "none",
                borderRadius: 14,
                padding: "14px 16px",
                color: "#fff",
                fontSize: 16,
                fontWeight: 700,
                background: "var(--primary)",
              }}
            >
              Get to Home
            </button>
          </div>
        </>
      )}
    </div>
  );
}
